import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PublicLayout } from './PublicLayout';
import { cancelByToken, fetchBookingByToken, publicErrorMessage } from './api';

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CancelPage() {
  const { t } = useTranslation();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const qc = useQueryClient();
  const [done, setDone] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public', 'booking', token],
    queryFn: () => fetchBookingByToken(token),
    enabled: token.length > 0 && !done,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => cancelByToken(token),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['public', 'booking', token] });
      setDone(true);
    },
  });

  if (done) {
    return (
      <PublicLayout>
        <div className="rounded-lg bg-emerald-50 p-6 ring-1 ring-emerald-300 dark:bg-emerald-950/40 dark:ring-emerald-800">
          <h1 className="text-2xl font-semibold text-emerald-900 dark:text-emerald-200">
            {t('publicBooking.cancel.successTitle')}
          </h1>
          <p className="mt-1 text-sm text-emerald-800 dark:text-emerald-300">
            {t('publicBooking.cancel.successSubtitle')}
          </p>
          <div className="mt-4">
            <Link
              to="/rezerwacja"
              className="inline-flex rounded-md bg-white px-3 py-2 text-sm text-brand-700 ring-1 ring-brand-300 hover:bg-brand-50 dark:bg-zinc-900 dark:text-brand-300 dark:ring-brand-800 dark:hover:bg-brand-950/40"
            >
              {t('publicBooking.publicLink')}
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (isLoading) {
    return (
      <PublicLayout>
        <p className="dark:text-zinc-300">{t('publicBooking.loading')}</p>
      </PublicLayout>
    );
  }

  if (error || !data) {
    return (
      <PublicLayout>
        <div className="rounded-lg bg-zinc-50 p-6 ring-1 ring-zinc-200 dark:bg-zinc-900/50 dark:ring-zinc-800">
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
            {t('publicBooking.cancel.alreadyTitle')}
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            {t('publicBooking.cancel.alreadySubtitle')}
          </p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="space-y-4">
        <header>
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
            {t('publicBooking.cancel.title')}
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.cancel.subtitle')}</p>
        </header>

        <dl className="space-y-2 rounded-lg bg-white p-4 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-800">
          <div className="flex justify-between gap-3">
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.when')}</dt>
            <dd className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {formatDateTime(data.startAt)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.who')}</dt>
            <dd className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {data.employee.firstName} {data.employee.lastName}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">{t('publicBooking.success.what')}</dt>
            <dd className="mt-1">
              <ul className="space-y-1 text-sm text-zinc-800 dark:text-zinc-200">
                {data.services.map((s) => (
                  <li key={s.id}>· {s.name}</li>
                ))}
              </ul>
            </dd>
          </div>
        </dl>

        {mutation.isError ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
            {publicErrorMessage(mutation.error, t('publicBooking.errorGeneric'))}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Link
            to="/rezerwacja"
            className="rounded-lg bg-white px-4 py-2 text-sm text-zinc-700 ring-1 ring-zinc-300 transition hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
          >
            {t('publicBooking.cancel.back')}
          </Link>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-zinc-400 dark:disabled:bg-zinc-700 dark:disabled:text-zinc-500"
          >
            {mutation.isPending ? t('publicBooking.submitting') : t('publicBooking.cancel.confirm')}
          </button>
        </div>
      </section>
    </PublicLayout>
  );
}
